/**
 * Iter 74d — CarReviewsList with vendor reply support.
 *
 * Renders each guest review with the vendor's public reply beneath it (if
 * present). When the current user owns the car, an inline reply form lets
 * the vendor add / edit / remove a one-line response without leaving the
 * page. Optimistic UI keeps the state in sync without a full reload.
 */
import { useState } from "react";
import { Star, MessageCircle, Pencil, Trash2, BadgeCheck } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

function VendorReplyBlock({ review, canReply, onUpdated }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(review.vendor_reply || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const text = draft.trim();
    if (!text) {
      toast.error("Reply cannot be empty");
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post(`/reviews/${review.id}/reply`, { reply: text });
      onUpdated?.(review.id, data);
      setEditing(false);
      toast.success("Reply posted");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setSaving(true);
    try {
      await api.delete(`/reviews/${review.id}/reply`);
      onUpdated?.(review.id, { ...review, vendor_reply: null, vendor_reply_at: null });
      setDraft("");
      setEditing(false);
      toast.success("Reply removed");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  // Existing reply — read-only display + edit/delete for the owner.
  if (review.vendor_reply && !editing) {
    return (
      <div
        className="mt-3 ml-12 rounded-2xl bg-neutral-50 border border-neutral-200 p-3"
        data-testid={`vendor-reply-${review.id}`}
      >
        <div className="flex items-center gap-1.5 text-[11px] font-semibold text-neutral-500 uppercase tracking-wider">
          <MessageCircle className="h-3 w-3" />
          Host response
        </div>
        <p className="text-sm text-neutral-800 mt-1 whitespace-pre-wrap">{review.vendor_reply}</p>
        {canReply && (
          <div className="flex gap-2 mt-2">
            <button
              type="button"
              onClick={() => { setDraft(review.vendor_reply); setEditing(true); }}
              className="inline-flex items-center gap-1 text-[11px] text-neutral-600 hover:text-neutral-900"
              data-testid={`vendor-reply-edit-${review.id}`}
            >
              <Pencil className="h-3 w-3" /> Edit
            </button>
            <button
              type="button"
              onClick={remove}
              disabled={saving}
              className="inline-flex items-center gap-1 text-[11px] text-rose-600 hover:text-rose-800 disabled:opacity-50"
              data-testid={`vendor-reply-remove-${review.id}`}
            >
              <Trash2 className="h-3 w-3" /> Remove
            </button>
          </div>
        )}
      </div>
    );
  }

  // No reply yet, and current user is the owner → show inline form.
  // OR: editing existing reply.
  if (canReply) {
    return (
      <div
        className="mt-3 ml-12 rounded-2xl bg-blue-50/60 border border-blue-100 p-3"
        data-testid={`vendor-reply-form-${review.id}`}
      >
        <div className="text-[11px] font-semibold text-blue-800 uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
          <MessageCircle className="h-3 w-3" />
          {editing ? "Edit your reply" : "Reply as the host"}
        </div>
        <textarea
          rows={2}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Thank the guest, or clarify anything — visible under the review."
          maxLength={600}
          className="w-full rounded-xl border border-blue-200 bg-white p-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          data-testid={`vendor-reply-input-${review.id}`}
        />
        <div className="flex gap-2 mt-2 items-center">
          <button
            type="button"
            onClick={save}
            disabled={saving || !draft.trim()}
            className="px-3 py-1 rounded-full bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid={`vendor-reply-submit-${review.id}`}
          >
            {saving ? "Saving…" : editing ? "Save" : "Post reply"}
          </button>
          {editing && (
            <button
              type="button"
              onClick={() => { setEditing(false); setDraft(review.vendor_reply || ""); }}
              className="text-xs text-neutral-600 hover:text-neutral-900"
            >
              Cancel
            </button>
          )}
          <span className="ml-auto text-[10px] text-neutral-500">{draft.length}/600</span>
        </div>
      </div>
    );
  }

  return null;
}

export default function CarReviewsList({ reviews = [], carVendorId = null }) {
  const { user } = useAuth();
  const [list, setList] = useState(reviews);

  // Keep local state in sync when parent-provided reviews change (e.g. car
  // detail page reloads reviews after navigation).
  if (list !== reviews && list.length === 0 && reviews.length > 0) {
    setList(reviews);
  }

  const isOwner = !!(user && carVendorId && user.role === "vendor" && user.id === carVendorId);

  const handleReplyUpdated = (reviewId, updated) => {
    setList((prev) => prev.map((r) => (r.id === reviewId ? { ...r, ...updated } : r)));
  };

  return (
    <div className="py-8">
      <h3 className="font-display text-xl font-medium mb-6">Reviews</h3>
      {list.length === 0 ? (
        <div className="text-neutral-500 text-sm">No reviews yet.</div>
      ) : (
        <div className="space-y-6">
          {list.map((r) => (
            <div key={r.id} className="pb-6 border-b border-border last:border-0">
              <div className="flex items-center gap-3 mb-2">
                <div className="h-9 w-9 rounded-full bg-neutral-200 flex items-center justify-center text-xs font-semibold">{r.user_name?.[0]}</div>
                <div>
                  <div className="text-sm font-medium flex items-center gap-1.5">
                    {r.user_name}
                    {r.is_verified && (
                      <span
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-[10px] font-semibold"
                        title="This guest actually rented and paid — booking verified by RentalCar.al"
                        data-testid={`review-verified-${r.id}`}
                      >
                        <BadgeCheck className="h-3 w-3" />
                        Verified guest
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1 text-xs text-neutral-500">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className={`h-3 w-3 ${i < r.rating ? "fill-neutral-900 text-neutral-900" : "text-neutral-300"}`} />
                    ))}
                  </div>
                </div>
              </div>
              {r.comment && (
                <p className="text-sm text-neutral-700 whitespace-pre-wrap">{r.comment}</p>
              )}
              <VendorReplyBlock
                review={r}
                canReply={isOwner}
                onUpdated={handleReplyUpdated}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
